import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowClockwise, Trash, FilePdf, X, CheckCircle, WarningCircle, CircleNotch, ArrowSquareOut } from "@phosphor-icons/react";
import { AdminHeader, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage, API, ADMIN_TOKEN_KEY } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = {
  synced: { label: "Synced to Monday", cls: "text-[#73bf44] bg-[#73bf44]/10 border-[#73bf44]/30" },
  pending: { label: "Syncing…", cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  partial: { label: "Partial (files)", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  failed: { label: "Sync failed", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  not_configured: { label: "Monday not configured", cls: "text-neutral-400 bg-neutral-800/60 border-neutral-700" },
};

function StatusBadge({ s }) {
  const m = STATUS[s] || STATUS.pending;
  return <span className={`inline-flex items-center gap-1.5 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full border ${m.cls}`}>{m.label}</span>;
}

export default function AdminWarrantyClaims() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/warranty-claims")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(getErrorMessage(e, "Couldn't load warranty claims. Check your connection and retry.")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const retry = async (r) => {
    setBusyId(r.id);
    try {
      await adminApi.post(`/admin/warranty-claims/${r.id}/retry`);
      toast.success("Re-syncing to Monday…");
      setTimeout(load, 3500);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusyId(null); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete warranty claim from ${r.customer_name || r.chassis_number}? This does not remove the Monday card.`)) return;
    await adminApi.delete(`/admin/warranty-claims/${r.id}`);
    toast.success("Deleted"); setActive(null); load();
  };

  const fileUrl = (claim, i) => `${API}/admin/warranty-claims/${claim.id}/file/${i}?auth=${token}`;

  return (
    <div>
      <AdminHeader
        title="Warranty Claims"
        subtitle="Claims customers submit directly through the OTM Tech site — saved here and pushed to the Monday warranty board."
      />
      <div className="p-8">
        {loading ? (
          <div className="text-neutral-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3" data-testid="warranty-empty">
            <ShieldCheck size={36} className="text-neutral-700" weight="duotone" /> No warranty claims yet.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="warranty-table">
              <thead>
                <tr className="text-left text-[0.65rem] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="px-5 py-3 font-semibold">Chassis / Owner</th>
                  <th className="px-5 py-3 font-semibold">Model</th>
                  <th className="px-5 py-3 font-semibold">Dealer</th>
                  <th className="px-5 py-3 font-semibold">Submitted</th>
                  <th className="px-5 py-3 font-semibold">Monday</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {rows.map((r) => (
                  <tr key={r.id} data-testid="warranty-row" className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setActive(r)}>
                    <td className="px-5 py-4">
                      <div className="font-mono text-[#73bf44]">{r.chassis_number}</div>
                      <div className="text-neutral-300">{r.customer_name}</div>
                    </td>
                    <td className="px-5 py-4 text-neutral-300">{r.model || "—"}</td>
                    <td className="px-5 py-4 text-neutral-400">{r.dealer || "—"}</td>
                    <td className="px-5 py-4 text-neutral-500 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-4"><StatusBadge s={r.monday_status} /></td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {(r.monday_status === "failed" || r.monday_status === "partial" || r.monday_status === "not_configured") && (
                          <button onClick={() => retry(r)} disabled={busyId === r.id} data-testid="warranty-retry" title="Retry Monday sync" className="p-2 rounded-lg text-neutral-400 hover:text-[#73bf44] hover:bg-[#73bf44]/10">
                            {busyId === r.id ? <CircleNotch size={16} className="animate-spin" /> : <ArrowClockwise size={16} />}
                          </button>
                        )}
                        <button onClick={() => del(r)} data-testid="warranty-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">Warranty Claim</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <StatusBadge s={active.monday_status} />
                {active.monday_item_id && <span className="text-xs text-neutral-500">Monday item #{active.monday_item_id}</span>}
              </div>
              {active.monday_error && (
                <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 flex items-start gap-2">
                  <WarningCircle size={15} className="mt-0.5 shrink-0" /> <span className="break-words">{active.monday_error}</span>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Detail label="OTM Chassis Number" value={active.chassis_number} mono />
                <Detail label="Model" value={active.model} />
                <Detail label="Caravan owner" value={active.customer_name} />
                <Detail label="Selling dealer" value={active.dealer} />
                <Detail label="Submitted by" value={active.submitted_by_name} />
                <Detail label="Contact email" value={active.submitted_by_email} />
                <Detail label="Total repair cost" value={active.total_repair_cost} />
                <Detail label="Submitted" value={(active.created_at || "").slice(0, 16).replace("T", " ")} />
              </div>
              <Block label="Warranty issue / fault" value={active.warranty_issues} />
              <Block label="Technical justification" value={active.technical_justification} />
              <Block label="Parts required by OTM" value={active.parts_required} />
              <Block label="Additional comments" value={active.additional_comments} />

              {(active.files || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Attachments</div>
                  <div className="flex flex-wrap gap-2">
                    {active.files.map((f, i) => (
                      f.kind === "quote" ? (
                        <a key={i} href={fileUrl(active, i)} target="_blank" rel="noreferrer" data-testid="warranty-view-quote" className="inline-flex items-center gap-1.5 text-sm bg-[#73bf44]/10 border border-[#73bf44]/30 text-[#73bf44] rounded-lg px-3 py-2 hover:bg-[#73bf44]/20">
                          <FilePdf size={16} weight="duotone" /> Repair quote (PDF) <ArrowSquareOut size={13} />
                        </a>
                      ) : (
                        <a key={i} href={fileUrl(active, i)} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-neutral-800 block">
                          <img src={fileUrl(active, i)} alt={f.filename} className="w-full h-full object-cover" />
                        </a>
                      )
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
                {(active.monday_status === "failed" || active.monday_status === "partial" || active.monday_status === "not_configured") && (
                  <GhostButton onClick={() => retry(active)} data-testid="warranty-detail-retry"><ArrowClockwise size={15} /> Retry sync</GhostButton>
                )}
                <PrimaryButton onClick={() => setActive(null)}>Close</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`text-white ${mono ? "font-mono text-[#73bf44]" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Block({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{label}</div>
      <div className="text-sm text-neutral-200 whitespace-pre-wrap bg-black/30 border border-neutral-800 rounded-lg px-3 py-2">{value}</div>
    </div>
  );
}
